"use client";

import { forwardRef, useEffect } from "react";
import "./ScreenOverlay.css";
import { csx } from "../../utils";
import { Portal } from "radix-ui";
import { useTheme, ThemeProvider } from "../ThemeProvider";

interface ScreenOverlayProps {
  /**
   * Children to render inside the overlay.
   */
  children?: React.ReactNode;

  /**
   * Whether to fade in the overlay.
   */
  fadeIn?: boolean;

  /**
   * Callback function to handle events (click or ESC key).
   */
  onClose?: () => void;

  /**
   * Ref to the overlay element.
   */
  ref?: React.RefObject<HTMLDivElement>;
}

/**
 * Base Overlay component for creating modal-like backgrounds.
 *
 * API:
 *  - {@link ScreenOverlayProps}
 */
export const ScreenOverlay = forwardRef<HTMLDivElement, ScreenOverlayProps>(
  ({ fadeIn = false, onClose, children }, ref) => {
    const { theme, palette } = useTheme();

    useEffect(() => {
      if (!onClose) return;

      const handleEscKey = (event: KeyboardEvent) => {
        if (event.key === "Escape") onClose();
      };

      document.addEventListener("keydown", handleEscKey);
      return () => {
        document.removeEventListener("keydown", handleEscKey);
      };
    }, [onClose]);

    return (
      <Portal.Root>
        <ThemeProvider theme={theme} palette={palette}>
          <div
            ref={ref}
            id="background"
            aria-label="Background overlay"
            role="dialog"
            aria-modal="true"
            data-testid="screen-overlay"
            className={csx(
              "proton-ScreenOverlay__background",
              "proton-ScreenOverlay__z-index",
              fadeIn && "proton-ScreenOverlay__fade-in"
            )}
            onClick={(event: React.MouseEvent) => {
              if (!onClose) return;

              // Only trigger onClose if the click was directly on the background element
              if (event.target === event.currentTarget) {
                onClose();
              }
            }}
          >
            {children}
          </div>
        </ThemeProvider>
      </Portal.Root>
    );
  }
);

ScreenOverlay.displayName = "ProtonUIScreenOverlay";
